import { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { TreeBrowser } from "./TreeBrowser";
import type { Container } from "@egalregal/shared";
import { useT } from "../../shared/i18n";

interface PickerDialogProps {
  open: boolean;
  title: string;
  containers: Container[];
  /** Containers that must not be offered (self + descendants, cycle-free moves). */
  excludeIds: Set<string>;
  onPick: (id: string) => void;
  onClose: () => void;
}

/**
 * Drill-down picker for link targets, reusing the same TreeBrowser that powers
 * the Browse view. (Move targets use the unified MoveEntryDialog — ticket 03.)
 */
export function PickerDialog({ open, title, containers, excludeIds, onPick, onClose }: PickerDialogProps) {
  const t = useT();
  const [currentId, setCurrentId] = useState<string | null>(null);
  const visible = containers.filter((c) => !excludeIds.has(c.id));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" TransitionProps={{ onExited: () => setCurrentId(null) }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TreeBrowser containers={visible} currentId={currentId} onSelect={(id) => setCurrentId(id)} showUrgency={false} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button
          variant="contained"
          disabled={currentId === null}
          onClick={() => { if (currentId !== null) { onPick(currentId); onClose(); } }}
        >
          {t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}